/** Formations v2 validation lab (port of qa/formations_lab.html, same
 *  address /qa/formations/<code>).
 *
 * Window-averaged shapes vs matched templates, v1/v2 timeline, and the
 * correct-formation ground-truth labeler. Data and label writes use the
 * same /api/v1/qa/formations endpoints the Jinja page used.
 */
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { api } from '../../lib/api'
import { NO_FRAME_RATE_NOTE, servedFps } from '../../lib/frameRate'
import { pathSeg } from '../../lib/urls'
import './qa.css'

type WindowRow = {
  team: number; window: number; state: string; phase: number
  start_frame: number; end_frame: number
  formation: string; confidence: number
  v1_formation: string | null; v1_share: number | null
  players_visible_mean: number; n_samples: number
}
type Shape = { team: number; window: number; state: string; x: number; y: number; track: number }
type Label = { team: number; window: number; state: string;
               correct_formation: string; labeled_by: string }
type Payload = {
  game_code: string; org_code: string; fps: number | null
  windows: WindowRow[]; shapes: Shape[]; labels: Label[]
  templates: Record<string, [number, number][]>
  formation_options: string[]
}

function colorFor(name: string): string {
  let h = 0
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) % 360
  return `hsl(${h}, 65%, 45%)`
}

const px = (x: number) => 25 + (x + 52.5) / 105 * 1000
const py = (y: number) => 15 + (y + 34) / 68 * 650
const lo = (a: number[]) => Math.min(...a)
const hi = (a: number[]) => Math.max(...a)

export default function FormationsLab() {
  const { gameCode = '' } = useParams<{ gameCode: string }>()
  const [data, setData] = useState<Payload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [team, setTeam] = useState(0)
  const [state, setState] = useState('out')
  const [current, setCurrent] = useState(0)
  const [labelChoice, setLabelChoice] = useState('')
  const [labelStatus, setLabelStatus] = useState('')

  useEffect(() => {
    api<Payload>(`/api/v1/qa/formations/${pathSeg(gameCode)}`)
      .then((d) => {
        setData(d)
        const teams = [...new Set(d.windows.map((w) => w.team))].sort()
        if (teams.length) setTeam(teams[0]!)
      })
      .catch((e: Error) => setError(e.message))
  }, [gameCode])

  const rows = useMemo(() => {
    if (!data) return []
    return data.windows.filter((w) => w.team === team && w.state === state)
      .sort((a, b) => a.window - b.window)
  }, [data, team, state])
  const idx = Math.min(current, Math.max(rows.length - 1, 0))
  const r = rows[idx]

  const labelFor = (row: WindowRow) => data?.labels.find(
    (l) => l.team === row.team && l.window === row.window && l.state === row.state)

  // Keep the label select synced to the current window.
  useEffect(() => {
    if (!r) return
    const lbl = labelFor(r)
    setLabelChoice(lbl ? lbl.correct_formation : r.formation)
    setLabelStatus(lbl ? `labeled ${lbl.correct_formation} by ${lbl.labeled_by.split('@')[0]}` : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r?.team, r?.window, r?.state, data])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setCurrent((c) => Math.max(0, c - 1))
      if (e.key === 'ArrowRight') setCurrent((c) => Math.min(rows.length - 1, c + 1))
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [rows.length])

  const save = async () => {
    if (!r || !data) return
    setLabelStatus('saving...')
    try {
      await api(`/api/v1/qa/formations/${pathSeg(gameCode)}/label`, {
        method: 'POST',
        body: JSON.stringify({
          team: r.team, window: r.window, state: r.state,
          start_frame: r.start_frame, end_frame: r.end_frame,
          detected_formation: r.formation, correct_formation: labelChoice,
        }),
      })
      const existing = labelFor(r)
      if (existing) { existing.correct_formation = labelChoice; existing.labeled_by = 'you' }
      else data.labels.push({ team: r.team, window: r.window, state: r.state,
                              correct_formation: labelChoice, labeled_by: 'you' })
      setData({ ...data })
      setLabelStatus(`labeled ${labelChoice}`)
    } catch {
      setLabelStatus('save failed')
    }
  }

  if (error) return <div className="qa-lab-wrap"><div className="err">{error}</div></div>
  if (!data) return <div className="qa-lab-wrap"><div className="sub">{gameCode} - loading...</div></div>

  const teams = [...new Set(data.windows.map((w) => w.team))].sort()
  const shapePts = r ? data.shapes.filter(
    (s) => s.team === r.team && s.window === r.window && s.state === r.state) : []

  // Template ghosts fitted to the team's actual bounding box.
  let ghosts: [number, number][] = []
  if (r && shapePts.length) {
    const xs = shapePts.map((p) => p.x)
    const ys = shapePts.map((p) => p.y)
    const xr: [number, number] = [lo(xs), hi(xs)]
    const yr: [number, number] = [lo(ys), hi(ys)]
    const t = data.templates[r.formation] || []
    if (t.length) {
      const txs = t.map((p) => p[0])
      const tys = t.map((p) => p[1])
      const txr: [number, number] = [lo(txs), hi(txs)]
      const tyr: [number, number] = [lo(tys), hi(tys)]
      ghosts = t.map(([tx, ty]) => [
        xr[0] + (tx - txr[0]) / ((txr[1] - txr[0]) || 1) * (xr[1] - xr[0]),
        yr[0] + (ty - tyr[0]) / ((tyr[1] - tyr[0]) || 1) * (yr[1] - yr[0]),
      ])
    }
  }

  // the game's recorded rate; with none a window is not given in minutes
  const fps = servedFps(data.fps)
  const mins = (f: number) => (fps === null ? null : (f / fps / 60).toFixed(1))

  return (
    <div className="qa-lab-wrap">
      <div className="wrap">
        <h2>Formations Lab</h2>
        <div className="sub">
          {data.game_code} ({data.org_code}) - {data.windows.length} window rows,{' '}
          {data.labels.length} labeled
        </div>

        <div className="controls">
          <label>team{' '}
            <select value={team} onChange={(e) => { setTeam(Number(e.target.value)); setCurrent(0) }}>
              {teams.map((t) => <option key={t} value={t}>team {t}</option>)}
            </select>
          </label>
          <label>state{' '}
            <select value={state} onChange={(e) => { setState(e.target.value); setCurrent(0) }}>
              <option value="in">in possession</option>
              <option value="out">out of possession</option>
            </select>
          </label>
          <button onClick={() => setCurrent((c) => Math.max(0, c - 1))}>← prev</button>
          <button onClick={() => setCurrent((c) => Math.min(rows.length - 1, c + 1))}>next →</button>
          <span className="sub windowinfo">
            {r ? `window ${idx + 1}/${rows.length} - phase ${r.phase}` +
                 (fps === null ? ` - ${NO_FRAME_RATE_NOTE}`
                   : ` - ${mins(r.start_frame)}-${mins(r.end_frame)} min`) : ''}
          </span>
        </div>

        <div className="panel">
          <div className="info">
            <span>v2 detected <b>{r ? r.formation : 'no data'}</b></span>
            <span>confidence <b>{r ? r.confidence.toFixed(2) : '-'}</b></span>
            <span>v1 says <b className="v1">{r?.v1_formation
              ? `${r.v1_formation} (${Math.round((r.v1_share || 0) * 100)}%)` : 'n/a'}</b></span>
            <span>visible <b>{r ? r.players_visible_mean.toFixed(0) : '-'}</b></span>
            <span>samples <b>{r ? r.n_samples : '-'}</b></span>
            <span className="labeler">
              correct:{' '}
              <select value={labelChoice} onChange={(e) => setLabelChoice(e.target.value)}>
                {data.formation_options.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>{' '}
              <button onClick={save}>save</button>{' '}
              <span className="labelstatus">{labelStatus}</span>
            </span>
          </div>

          <svg id="pitch" viewBox="0 0 1050 680">
            <rect x="0" y="0" width="1050" height="680" fill="#0b6e2f" />
            <rect x="25" y="15" width="1000" height="650" fill="none" stroke="#ffffff88" strokeWidth="2" />
            <line x1="525" y1="15" x2="525" y2="665" stroke="#ffffff88" strokeWidth="2" />
            <circle cx="525" cy="340" r="80" fill="none" stroke="#ffffff88" strokeWidth="2" />
            <polygon points="1000,330 1020,340 1000,350" fill="#ffffffcc" />
            {ghosts.map(([gx, gy], i) => (
              <circle key={`g${i}`} cx={px(gx)} cy={py(gy)} r="14" fill="none"
                      stroke="#ffe066" strokeWidth="3" opacity="0.9" />
            ))}
            {shapePts.map((s, i) => (
              <g key={i}>
                <circle cx={px(s.x)} cy={py(s.y)} r="13" fill="#ffffff"
                        stroke="#121417" strokeWidth="1.5" />
                <text x={px(s.x)} y={py(s.y) + 4} textAnchor="middle" fontSize="12"
                      fontWeight="600" fill="#121417">{s.track}</text>
              </g>
            ))}
          </svg>
          <div className="legend">
            solid dots = window-averaged player positions at true pitch scale (attacking →),
            jersey/slot number inside. GK excluded by design. hollow rings = matched template
            slots fitted to the team's actual footprint.
          </div>

          <svg id="timeline" viewBox={`0 0 1000 76`} preserveAspectRatio="none">
            <text x="0" y="14" fontSize="10" fill="#6b7280">v2</text>
            <text x="0" y="48" fontSize="10" fill="#6b7280">v1</text>
            {rows.map((row, i) => {
              const bw = 984 / rows.length
              const x = i * bw + 16
              const lbl = labelFor(row)
              return (
                <g key={i} onClick={() => setCurrent(i)} style={{ cursor: 'pointer' }}>
                  <rect x={x} y="4" width={Math.max(bw - 1, 1)} height="26"
                        fill={colorFor(row.formation)}
                        opacity={Math.min(0.35 + row.confidence * 4, 1)} />
                  {row.v1_formation && (
                    <rect x={x} y="36" width={Math.max(bw - 1, 1)} height="26"
                          fill={colorFor(row.v1_formation)}
                          opacity={Math.min(0.35 + (row.v1_share || 0.5), 1)} />
                  )}
                  {lbl && (
                    <circle cx={x + bw / 2} cy="70" r="2.5"
                            fill={lbl.correct_formation === row.formation ? '#047857' : '#b91c1c'} />
                  )}
                  {i === idx && (
                    <rect x={x} y="66" width={Math.max(bw - 1, 1)} height="6" fill="#121417" />
                  )}
                </g>
              )
            })}
          </svg>
          <div className="legend">
            timeline: top row = v2, bottom row = v1 (same window, same possession state),
            color = formation, opacity = confidence/share. Click to jump.
          </div>
        </div>
      </div>
    </div>
  )
}
